import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { 
    collection, addDoc, increment, serverTimestamp, query, 
    orderBy, onSnapshot, doc, updateDoc, deleteDoc,
    where, getDocs, limit 
} from 'firebase/firestore';
import './Prayers.css';

const Prayers = () => {
    const { currentUser, userData } = useAuth();
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState('prayer-requests');
    const [loading, setLoading] = useState(false);
    const [prayerRequests, setPrayerRequests] = useState([]);
    const [testimonies, setTestimonies] = useState([]);
    const [meetingLink, setMeetingLink] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    
    // Prayer request form
    const [prayerFormData, setPrayerFormData] = useState({
        title: '',
        content: '',
        isAnonymous: true,
        category: 'general'
    });
    
    // Testimony form
    const [testimonyFormData, setTestimonyFormData] = useState({
        title: '',
        content: '',
        isAnonymous: true,
        answeredDate: ''
    });
    
    // Meeting form (admin only)
    const [meetingFormData, setMeetingFormData] = useState({
        link: '',
        scheduledTime: '',
        description: ''
    });
    
    const [showPrayerForm, setShowPrayerForm] = useState(false);
    const [showTestimonyForm, setShowTestimonyForm] = useState(false);
    const [showMeetingForm, setShowMeetingForm] = useState(false);
    const [prayingFor, setPrayingFor] = useState({});
    
    // Edit states
    const [editingPrayer, setEditingPrayer] = useState(null);
    const [editingTestimony, setEditingTestimony] = useState(null);
    const [showEditPrayerForm, setShowEditPrayerForm] = useState(false);
    const [showEditTestimonyForm, setShowEditTestimonyForm] = useState(false);
    
    // Categories for prayer requests
    const prayerCategories = [
        { value: 'general', label: 'General Prayer Request' },
        { value: 'health', label: 'Health & Healing' },
        { value: 'family', label: 'Family Matters' },
        { value: 'financial', label: 'Financial Breakthrough' },
        { value: 'career', label: 'Career & Work' },
        { value: 'spiritual', label: 'Spiritual Growth' },
        { value: 'relationship', label: 'Relationships' },
        { value: 'other', label: 'Other' }
    ];
    
    // Check if user is admin
    useEffect(() => {
        const checkAdminStatus = async () => {
            if (currentUser) {
                // Check if user has admin role (customize based on your admin system)
                const isUserAdmin = userData?.role === 'admin' || userData?.isAdmin === true;
                setIsAdmin(isUserAdmin);
            }
        };
        checkAdminStatus();
    }, [currentUser, userData]);
    
    // Fetch prayer requests
    useEffect(() => {
        const q = query(
            collection(db, 'prayerRequests'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const requests = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date()
            }));
            setPrayerRequests(requests);
        }, (error) => {
            console.error('Error fetching prayer requests:', error);
        });
        
        return () => unsubscribe();
    }, []);
    
    // Fetch testimonies
    useEffect(() => {
        const q = query(
            collection(db, 'testimonies'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const testimoniesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date()
            }));
            setTestimonies(testimoniesList);
        }, (error) => {
            console.error('Error fetching testimonies:', error);
        });
        
        return () => unsubscribe();
    }, []);
    
    // Fetch active meeting link
    useEffect(() => {
        const fetchMeetingLink = async () => {
            try {
                const q = query(
                    collection(db, 'meetings'),
                    where('isActive', '==', true),
                    orderBy('createdAt', 'desc'),
                    limit(1)
                );
                
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    const meeting = querySnapshot.docs[0];
                    const meetingData = {
                        id: meeting.id,
                        ...meeting.data(),
                        scheduledTime: meeting.data().scheduledTime?.toDate() || null
                    };
                    
                    // Check if meeting is still valid (not expired)
                    if (meetingData.expiresAt?.toDate() > new Date()) {
                        setMeetingLink(meetingData);
                    } else if (meetingData.expiresAt?.toDate() <= new Date()) {
                        // Mark as inactive if expired
                        await updateDoc(doc(db, 'meetings', meeting.id), {
                            isActive: false
                        });
                        setMeetingLink(null);
                    }
                }
            } catch (error) {
                console.error('Error fetching meeting link:', error);
            }
        };
        
        fetchMeetingLink();
    }, []);
    
    const handlePrayerInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPrayerFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };
    
    const handleTestimonyInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTestimonyFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };
    
    const handleMeetingInputChange = (e) => {
        const { name, value } = e.target;
        setMeetingFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    // Check if user owns a prayer request (even if anonymous)
    const canEditPrayer = (prayer) => {
        if (!currentUser) return false;
        if (isAdmin) return true;
        // Check by userId regardless of anonymous status
        return prayer.userId === currentUser.uid;
    };
    
    // Check if user owns a testimony (even if anonymous)
    const canEditTestimony = (testimony) => {
        if (!currentUser) return false;
        if (isAdmin) return true;
        // Check by userId regardless of anonymous status
        return testimony.userId === currentUser.uid;
    };
    
    // Submit prayer request
    const handlePrayerSubmit = async (e) => {
        e.preventDefault();
        
        if (!prayerFormData.title.trim() || !prayerFormData.content.trim()) {
            showToast('Please fill in all fields', 'error');
            return;
        }
        
        setLoading(true);
        
        try {
            const prayerData = {
                title: prayerFormData.title,
                content: prayerFormData.content,
                category: prayerFormData.category,
                isAnonymous: prayerFormData.isAnonymous,
                userId: currentUser?.uid, // Always store userId for ownership tracking
                userName: prayerFormData.isAnonymous ? 'Anonymous' : (userData?.fullName || currentUser?.email),
                userEmail: prayerFormData.isAnonymous ? null : currentUser?.email,
                prayerCount: 0,
                status: 'active',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };
            
            const docRef = await addDoc(collection(db, 'prayerRequests'), prayerData);
            
            // Send notifications to admins and prayer warriors
            await fetch('/.netlify/functions/send-prayer-notification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'prayer_request',
                    prayerRequestId: docRef.id,
                    title: prayerFormData.title,
                    content: prayerFormData.content,
                    userName: prayerData.userName,
                    category: prayerFormData.category
                })
            });
            
            showToast('Prayer request submitted successfully!', 'success');
            setPrayerFormData({
                title: '',
                content: '',
                isAnonymous: true,
                category: 'general'
            });
            setShowPrayerForm(false);
        } catch (error) {
            console.error('Error submitting prayer request:', error);
            showToast('Error submitting prayer request', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    // Submit testimony
    const handleTestimonySubmit = async (e) => {
        e.preventDefault();
        
        if (!testimonyFormData.title.trim() || !testimonyFormData.content.trim()) {
            showToast('Please fill in all fields', 'error');
            return;
        }
        
        setLoading(true);
        
        try {
            const testimonyData = {
                title: testimonyFormData.title,
                content: testimonyFormData.content,
                isAnonymous: testimonyFormData.isAnonymous,
                answeredDate: testimonyFormData.answeredDate ? new Date(testimonyFormData.answeredDate) : null,
                userId: currentUser?.uid, // Always store userId for ownership tracking
                userName: testimonyFormData.isAnonymous ? 'Anonymous' : (userData?.fullName || currentUser?.email),
                userEmail: testimonyFormData.isAnonymous ? null : currentUser?.email,
                likes: 0,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };
            
            const docRef = await addDoc(collection(db, 'testimonies'), testimonyData);
            
            // Send notification about testimony
            await fetch('/.netlify/functions/send-notification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'testimony',
                    testimonyId: docRef.id,
                    title: testimonyFormData.title,
                    content: testimonyFormData.content,
                    userName: testimonyData.userName
                })
            });
            
            showToast('Testimony shared successfully! Praise God!', 'success');
            setTestimonyFormData({
                title: '',
                content: '',
                isAnonymous: true,
                answeredDate: ''
            });
            setShowTestimonyForm(false);
        } catch (error) {
            console.error('Error submitting testimony:', error);
            showToast('Error submitting testimony', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    // Update prayer request
    const handleUpdatePrayer = async (e) => {
        e.preventDefault();
        
        if (!editingPrayer.title.trim() || !editingPrayer.content.trim()) {
            showToast('Please fill in all fields', 'error');
            return;
        }
        
        setLoading(true);
        
        try {
            const prayerRef = doc(db, 'prayerRequests', editingPrayer.id);
            await updateDoc(prayerRef, {
                title: editingPrayer.title,
                content: editingPrayer.content,
                category: editingPrayer.category,
                isAnonymous: editingPrayer.isAnonymous,
                updatedAt: serverTimestamp()
            });
            
            showToast('Prayer request updated successfully!', 'success');
            setEditingPrayer(null);
            setShowEditPrayerForm(false);
        } catch (error) {
            console.error('Error updating prayer request:', error);
            showToast('Error updating prayer request', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    // Update testimony
    const handleUpdateTestimony = async (e) => {
        e.preventDefault();
        
        if (!editingTestimony.title.trim() || !editingTestimony.content.trim()) {
            showToast('Please fill in all fields', 'error');
            return;
        }
        
        setLoading(true);
        
        try {
            const testimonyRef = doc(db, 'testimonies', editingTestimony.id);
            await updateDoc(testimonyRef, {
                title: editingTestimony.title,
                content: editingTestimony.content,
                isAnonymous: editingTestimony.isAnonymous,
                answeredDate: editingTestimony.answeredDate ? new Date(editingTestimony.answeredDate) : null,
                updatedAt: serverTimestamp()
            });
            
            showToast('Testimony updated successfully!', 'success');
            setEditingTestimony(null);
            setShowEditTestimonyForm(false);
        } catch (error) {
            console.error('Error updating testimony:', error);
            showToast('Error updating testimony', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    // Delete prayer request
    const handleDeletePrayer = async (prayerId) => {
        if (window.confirm('Are you sure you want to delete this prayer request? This action cannot be undone.')) {
            setLoading(true);
            try {
                await deleteDoc(doc(db, 'prayerRequests', prayerId));
                showToast('Prayer request deleted successfully', 'success');
            } catch (error) {
                console.error('Error deleting prayer request:', error);
                showToast('Error deleting prayer request', 'error');
            } finally {
                setLoading(false);
            }
        }
    };
    
    // Delete testimony
    const handleDeleteTestimony = async (testimonyId) => {
        if (window.confirm('Are you sure you want to delete this testimony? This action cannot be undone.')) {
            setLoading(true);
            try {
                await deleteDoc(doc(db, 'testimonies', testimonyId));
                showToast('Testimony deleted successfully', 'success');
            } catch (error) {
                console.error('Error deleting testimony:', error);
                showToast('Error deleting testimony', 'error');
            } finally {
                setLoading(false);
            }
        }
    };
    
    // Create meeting (admin only)
    const handleCreateMeeting = async (e) => {
        e.preventDefault();
        
        if (!meetingFormData.link.trim()) {
            showToast('Please enter a valid Google Meet link', 'error');
            return;
        }
        
        setLoading(true);
        
        try {
            // Validate Google Meet link
            const meetRegex = /^(https?:\/\/)?(meet\.google\.com\/[a-z-]+|meet\.google\.com\/[a-z-]+\/[a-z-]+)/i;
            if (!meetRegex.test(meetingFormData.link)) {
                showToast('Please enter a valid Google Meet link', 'error');
                setLoading(false);
                return;
            }
            
            const meetingData = {
                link: meetingFormData.link,
                description: meetingFormData.description,
                scheduledTime: meetingFormData.scheduledTime ? new Date(meetingFormData.scheduledTime) : null,
                isActive: true,
                createdBy: currentUser?.uid,
                createdByName: userData?.fullName || currentUser?.email,
                createdAt: serverTimestamp(),
                expiresAt: meetingFormData.scheduledTime ? 
                    new Date(new Date(meetingFormData.scheduledTime).getTime() + 2 * 60 * 60 * 1000) : // 2 hours after scheduled time
                    new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours if no schedule
            };
            
            // Deactivate any existing active meetings
            const activeMeetingsQuery = query(
                collection(db, 'meetings'),
                where('isActive', '==', true)
            );
            const activeMeetings = await getDocs(activeMeetingsQuery);
            for (const meeting of activeMeetings.docs) {
                await updateDoc(doc(db, 'meetings', meeting.id), {
                    isActive: false
                });
            }
            
            await addDoc(collection(db, 'meetings'), meetingData);
            
            // Send notification to all users about the new meeting
            await fetch('/.netlify/functions/send-prayer-notification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'meeting_created',
                    meetingLink: meetingFormData.link,
                    meetingTime: meetingFormData.scheduledTime,
                    description: meetingFormData.description
                })
            });
            
            showToast('Meeting created and notifications sent!', 'success');
            setMeetingFormData({
                link: '',
                scheduledTime: '',
                description: ''
            });
            setShowMeetingForm(false);
            
            // Refresh meeting link
            const updatedMeeting = {
                link: meetingFormData.link,
                description: meetingFormData.description,
                scheduledTime: meetingFormData.scheduledTime ? new Date(meetingFormData.scheduledTime) : null
            };
            setMeetingLink(updatedMeeting);
        } catch (error) {
            console.error('Error creating meeting:', error);
            showToast('Error creating meeting', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    // Mark as praying for a request
    const handlePrayForRequest = async (requestId, currentCount) => {
        if (prayingFor[requestId]) return;
        
        try {
            await updateDoc(doc(db, 'prayerRequests', requestId), {
                prayerCount: increment(1),
                lastPrayedAt: serverTimestamp()
            });
            
            setPrayingFor(prev => ({ ...prev, [requestId]: true }));
            showToast('Thank you for praying! 🙏', 'success');
            
            // Get the prayer request details for notification
            const prayerDoc = await getDocs(query(
                collection(db, 'prayerRequests'),
                where('__name__', '==', requestId)
            ));
            
            if (!prayerDoc.empty) {
                const prayerData = prayerDoc.docs[0].data();
                // Notify the user even if anonymous (they still have a userId)
                if (prayerData.userId && prayerData.userId !== currentUser?.uid) {
                    // Notify the user that someone prayed for them
                    await fetch('/.netlify/functions/send-prayer-notification', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            type: 'prayer_received',
                            prayerRequestId: requestId,
                            userId: prayerData.userId,
                            prayerTitle: prayerData.title
                        })
                    });
                }
            }
        } catch (error) {
            console.error('Error updating prayer count:', error);
            showToast('Error updating prayer count', 'error');
        }
    };
    
    // Like a testimony
    const handleLikeTestimony = async (testimonyId, currentLikes) => {
        try {
            await updateDoc(doc(db, 'testimonies', testimonyId), {
                likes: increment(1)
            });
            showToast('You appreciated this testimony! 🙌', 'success');
        } catch (error) {
            console.error('Error liking testimony:', error);
        }
    };
    
    const formatDate = (date) => {
        if (!date) return 'Date not specified';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    return (
        <div className="prayers-page">
            <div className="prayers-header">
                <h1 className="prayers-title">Prayer & Testimony Wall</h1>
                <p className="prayers-subtitle">
                    Share your prayer requests, praise God for answered prayers, 
                    and join our virtual prayer meetings
                </p>
            </div>
            
            {/* Google Meet Integration */}
            {meetingLink && (
                <div className="meeting-container">
                    <div className="meeting-info">
                        <div className="meeting-header">
                            <i className="fas fa-video"></i>
                            <h3>Live Prayer Meeting</h3>
                            {meetingLink.scheduledTime && (
                                <span className="meeting-time">
                                    <i className="far fa-calendar-alt"></i>
                                    {formatDate(meetingLink.scheduledTime)}
                                </span>
                            )}
                        </div>
                        {meetingLink.description && (
                            <p className="meeting-description">{meetingLink.description}</p>
                        )}
                        <div className="meeting-actions">
                            <a 
                                href={meetingLink.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="join-meeting-btn"
                            >
                                <i className="fas fa-video"></i>
                                Join Meeting
                            </a>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Admin Meeting Creation */}
            {isAdmin && (
                <div className="admin-meeting-section">
                    <button 
                        className="create-meeting-btn"
                        onClick={() => setShowMeetingForm(!showMeetingForm)}
                    >
                        <i className="fas fa-plus-circle"></i>
                        {showMeetingForm ? 'Cancel' : 'Create New Meeting'}
                    </button>
                    
                    {showMeetingForm && (
                        <div className="meeting-form-container">
                            <h3>Create Google Meet Session</h3>
                            <form onSubmit={handleCreateMeeting} className="meeting-form">
                                <div className="form-group">
                                    <label>
                                        <i className="fab fa-google"></i>
                                        Google Meet Link
                                    </label>
                                    <input
                                        type="url"
                                        name="link"
                                        value={meetingFormData.link}
                                        onChange={handleMeetingInputChange}
                                        placeholder="https://meet.google.com/xxx-xxxx-xxx"
                                        required
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>
                                        <i className="far fa-clock"></i>
                                        Scheduled Time (Optional)
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="scheduledTime"
                                        value={meetingFormData.scheduledTime}
                                        onChange={handleMeetingInputChange}
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>
                                        <i className="fas fa-info-circle"></i>
                                        Description
                                    </label>
                                    <textarea
                                        name="description"
                                        value={meetingFormData.description}
                                        onChange={handleMeetingInputChange}
                                        placeholder="What will be discussed? Any special prayer focus?"
                                        rows="3"
                                        className="form-textarea"
                                    />
                                </div>
                                
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? 'Creating...' : 'Create Meeting'}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            )}
            
            {/* Tabs */}
            <div className="prayers-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'prayer-requests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('prayer-requests')}
                >
                    <i className="fas fa-hands-praying"></i>
                    Prayer Requests
                    {prayerRequests.length > 0 && (
                        <span className="tab-count">{prayerRequests.length}</span>
                    )}
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'testimonies' ? 'active' : ''}`}
                    onClick={() => setActiveTab('testimonies')}
                >
                    <i className="fas fa-praise"></i>
                    Testimonies
                    {testimonies.length > 0 && (
                        <span className="tab-count">{testimonies.length}</span>
                    )}
                </button>
            </div>
            
            {/* Prayer Requests Tab */}
            {activeTab === 'prayer-requests' && (
                <div className="prayer-requests-section">
                    <div className="section-header">
                        <button 
                            className="add-request-btn"
                            onClick={() => setShowPrayerForm(!showPrayerForm)}
                        >
                            <i className="fas fa-plus-circle"></i>
                            {showPrayerForm ? 'Cancel' : 'Share Prayer Request'}
                        </button>
                    </div>
                    
                    {showPrayerForm && (
                        <div className="prayer-form-container">
                            <form onSubmit={handlePrayerSubmit} className="prayer-form">
                                <h3>Submit a Prayer Request</h3>
                                
                                <div className="form-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={prayerFormData.title}
                                        onChange={handlePrayerInputChange}
                                        placeholder="Brief title for your prayer request"
                                        required
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Prayer Request Details</label>
                                    <textarea
                                        name="content"
                                        value={prayerFormData.content}
                                        onChange={handlePrayerInputChange}
                                        placeholder="Share what you'd like the community to pray about..."
                                        rows="5"
                                        required
                                        className="form-textarea"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Category</label>
                                    <select
                                        name="category"
                                        value={prayerFormData.category}
                                        onChange={handlePrayerInputChange}
                                        className="form-select"
                                    >
                                        {prayerCategories.map(cat => (
                                            <option key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="isAnonymous"
                                            checked={prayerFormData.isAnonymous}
                                            onChange={handlePrayerInputChange}
                                        />
                                        Post anonymously
                                    </label>
                                    <small>Your name won't be displayed publicly</small>
                                </div>
                                
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? 'Submitting...' : 'Submit Prayer Request'}
                                </button>
                            </form>
                        </div>
                    )}
                    
                    {/* Edit Prayer Request Form */}
                    {showEditPrayerForm && editingPrayer && (
                        <div className="prayer-form-container">
                            <form onSubmit={handleUpdatePrayer} className="prayer-form">
                                <h3>Edit Prayer Request</h3>
                                
                                <div className="form-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={editingPrayer.title}
                                        onChange={(e) => setEditingPrayer({...editingPrayer, title: e.target.value})}
                                        required
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Prayer Request Details</label>
                                    <textarea
                                        name="content"
                                        value={editingPrayer.content}
                                        onChange={(e) => setEditingPrayer({...editingPrayer, content: e.target.value})}
                                        rows="5"
                                        required
                                        className="form-textarea"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Category</label>
                                    <select
                                        name="category"
                                        value={editingPrayer.category}
                                        onChange={(e) => setEditingPrayer({...editingPrayer, category: e.target.value})}
                                        className="form-select"
                                    >
                                        {prayerCategories.map(cat => (
                                            <option key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={editingPrayer.isAnonymous}
                                            onChange={(e) => setEditingPrayer({...editingPrayer, isAnonymous: e.target.checked})}
                                        />
                                        Post anonymously
                                    </label>
                                    <small>Your name won't be displayed publicly</small>
                                </div>
                                
                                <div className="form-actions">
                                    <button type="submit" className="submit-btn" disabled={loading}>
                                        {loading ? 'Updating...' : 'Update Prayer Request'}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="cancel-btn"
                                        onClick={() => {
                                            setEditingPrayer(null);
                                            setShowEditPrayerForm(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                    
                    <div className="prayer-requests-list">
                        {prayerRequests.length === 0 ? (
                            <div className="empty-state">
                                <i className="fas fa-hands-praying"></i>
                                <p>No prayer requests yet. Be the first to share!</p>
                            </div>
                        ) : (
                            prayerRequests.map(request => (
                                <div key={request.id} className="prayer-card">
                                    <div className="prayer-card-header">
                                        <div className="prayer-info">
                                            <span className="prayer-category">
                                                <i className="fas fa-tag"></i>
                                                {prayerCategories.find(c => c.value === request.category)?.label || request.category}
                                            </span>
                                            <span className="prayer-date">
                                                <i className="far fa-calendar-alt"></i>
                                                {formatDate(request.createdAt)}
                                            </span>
                                        </div>
                                        <div className="prayer-author">
                                            <i className="fas fa-user"></i>
                                            {request.isAnonymous ? 'Anonymous' : request.userName}
                                        </div>
                                    </div>
                                    
                                    <h4 className="prayer-title">{request.title}</h4>
                                    <p className="prayer-content">{request.content}</p>
                                    
                                    <div className="prayer-card-footer">
                                        <button 
                                            className={`pray-btn ${prayingFor[request.id] ? 'prayed' : ''}`}
                                            onClick={() => handlePrayForRequest(request.id, request.prayerCount)}
                                            disabled={prayingFor[request.id]}
                                        >
                                            <i className="fas fa-hands-praying"></i>
                                            {prayingFor[request.id] ? 'Prayed' : 'Pray for this'}
                                        </button>
                                        <div className="prayer-count">
                                            <i className="fas fa-praying-hands"></i>
                                            {request.prayerCount || 0} people praying
                                        </div>
                                        
                                        {/* Edit/Delete buttons for prayer request owner - visible even for anonymous posts */}
                                        {canEditPrayer(request) && (
                                            <div className="action-buttons">
                                                <button 
                                                    className="edit-btn"
                                                    onClick={() => {
                                                        setEditingPrayer({
                                                            id: request.id,
                                                            title: request.title,
                                                            content: request.content,
                                                            category: request.category,
                                                            isAnonymous: request.isAnonymous
                                                        });
                                                        setShowEditPrayerForm(true);
                                                        setShowPrayerForm(false);
                                                    }}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                    Edit
                                                </button>
                                                <button 
                                                    className="delete-btn"
                                                    onClick={() => handleDeletePrayer(request.id)}
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            
            {/* Testimonies Tab */}
            {activeTab === 'testimonies' && (
                <div className="testimonies-section">
                    <div className="section-header">
                        <button 
                            className="add-testimony-btn"
                            onClick={() => setShowTestimonyForm(!showTestimonyForm)}
                        >
                            <i className="fas fa-pen-fancy"></i>
                            {showTestimonyForm ? 'Cancel' : 'Share Testimony'}
                        </button>
                    </div>
                    
                    {showTestimonyForm && (
                        <div className="testimony-form-container">
                            <form onSubmit={handleTestimonySubmit} className="testimony-form">
                                <h3>Share Your Testimony</h3>
                                <p className="form-subtitle">Give praise to God for what He has done!</p>
                                
                                <div className="form-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={testimonyFormData.title}
                                        onChange={handleTestimonyInputChange}
                                        placeholder="Title of your testimony"
                                        required
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Testimony</label>
                                    <textarea
                                        name="content"
                                        value={testimonyFormData.content}
                                        onChange={handleTestimonyInputChange}
                                        placeholder="Share how God answered your prayer or worked in your life..."
                                        rows="6"
                                        required
                                        className="form-textarea"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Date Answered (Optional)</label>
                                    <input
                                        type="date"
                                        name="answeredDate"
                                        value={testimonyFormData.answeredDate}
                                        onChange={handleTestimonyInputChange}
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="isAnonymous"
                                            checked={testimonyFormData.isAnonymous}
                                            onChange={handleTestimonyInputChange}
                                        />
                                        Share anonymously
                                    </label>
                                    <small>Your name won't be displayed publicly</small>
                                </div>
                                
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? 'Sharing...' : 'Share Testimony'}
                                </button>
                            </form>
                        </div>
                    )}
                    
                    {/* Edit Testimony Form */}
                    {showEditTestimonyForm && editingTestimony && (
                        <div className="testimony-form-container">
                            <form onSubmit={handleUpdateTestimony} className="testimony-form">
                                <h3>Edit Testimony</h3>
                                
                                <div className="form-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={editingTestimony.title}
                                        onChange={(e) => setEditingTestimony({...editingTestimony, title: e.target.value})}
                                        required
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Testimony</label>
                                    <textarea
                                        name="content"
                                        value={editingTestimony.content}
                                        onChange={(e) => setEditingTestimony({...editingTestimony, content: e.target.value})}
                                        rows="6"
                                        required
                                        className="form-textarea"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Date Answered (Optional)</label>
                                    <input
                                        type="date"
                                        name="answeredDate"
                                        value={editingTestimony.answeredDate}
                                        onChange={(e) => setEditingTestimony({...editingTestimony, answeredDate: e.target.value})}
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={editingTestimony.isAnonymous}
                                            onChange={(e) => setEditingTestimony({...editingTestimony, isAnonymous: e.target.checked})}
                                        />
                                        Share anonymously
                                    </label>
                                    <small>Your name won't be displayed publicly</small>
                                </div>
                                
                                <div className="form-actions">
                                    <button type="submit" className="submit-btn" disabled={loading}>
                                        {loading ? 'Updating...' : 'Update Testimony'}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="cancel-btn"
                                        onClick={() => {
                                            setEditingTestimony(null);
                                            setShowEditTestimonyForm(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                    
                    <div className="testimonies-list">
                        {testimonies.length === 0 ? (
                            <div className="empty-state">
                                <i className="fas fa-church"></i>
                                <p>No testimonies yet. Be the first to share what God has done!</p>
                            </div>
                        ) : (
                            testimonies.map(testimony => (
                                <div key={testimony.id} className="testimony-card">
                                    <div className="testimony-card-header">
                                        <div className="testimony-author">
                                            <i className="fas fa-user-circle"></i>
                                            {testimony.isAnonymous ? 'Anonymous' : testimony.userName}
                                        </div>
                                        <div className="testimony-date">
                                            <i className="far fa-calendar-alt"></i>
                                            {formatDate(testimony.createdAt)}
                                        </div>
                                    </div>
                                    
                                    <h4 className="testimony-title">{testimony.title}</h4>
                                    <p className="testimony-content">{testimony.content}</p>
                                    
                                    {testimony.answeredDate && (
                                        <div className="testimony-answered-date">
                                            <i className="fas fa-check-circle"></i>
                                            Answered on: {new Date(testimony.answeredDate).toLocaleDateString()}
                                        </div>
                                    )}
                                    
                                    <div className="testimony-card-footer">
                                        <button 
                                            className="like-btn"
                                            onClick={() => handleLikeTestimony(testimony.id, testimony.likes)}
                                        >
                                            <i className="fas fa-heart"></i>
                                            {testimony.likes || 0} people were blessed
                                        </button>
                                        
                                        {/* Edit/Delete buttons for testimony owner - visible even for anonymous posts */}
                                        {canEditTestimony(testimony) && (
                                            <div className="action-buttons">
                                                <button 
                                                    className="edit-btn"
                                                    onClick={() => {
                                                        setEditingTestimony({
                                                            id: testimony.id,
                                                            title: testimony.title,
                                                            content: testimony.content,
                                                            isAnonymous: testimony.isAnonymous,
                                                            answeredDate: testimony.answeredDate ? new Date(testimony.answeredDate).toISOString().split('T')[0] : ''
                                                        });
                                                        setShowEditTestimonyForm(true);
                                                        setShowTestimonyForm(false);
                                                    }}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                    Edit
                                                </button>
                                                <button 
                                                    className="delete-btn"
                                                    onClick={() => handleDeleteTestimony(testimony.id)}
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            
            {/* Scripture Verse */}
            <div className="scripture-verse">
                <i className="fas fa-bible"></i>
                <p>"Therefore confess your sins to each other and pray for each other so that you may be healed. The prayer of a righteous person is powerful and effective."</p>
                <span>- James 5:16</span>
            </div>
        </div>
    );
};

export default Prayers;
